import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Gauge, Plus, Trash2, Pencil, Activity, Zap, Cable, Copy, ChevronDown, ChevronUp, LineChart as LineIcon } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid } from "recharts";

const emptyD = () => ({ naam: "", tag: "", eenheid: "bar", min: "", max: "" });
const num = (v) => (v === "" || v == null ? "" : Number(v));

const geleden = (ts) => {
  if (!ts) return "geen data";
  const s = Math.max(0, Math.round((Date.now() - new Date(ts).getTime()) / 1000));
  if (s < 60) return `${s}s geleden`;
  if (s < 3600) return `${Math.round(s / 60)} min geleden`;
  if (s < 86400) return `${Math.round(s / 3600)} uur geleden`;
  return `${Math.round(s / 86400)} d geleden`;
};

export default function TDLiveData() {
  const [devices, setDevices] = useState([]);
  const [form, setForm] = useState(emptyD());
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [chartId, setChartId] = useState(null);
  const [readings, setReadings] = useState([]);
  const [showKoppel, setShowKoppel] = useState(false);
  const [ingest, setIngest] = useState(null);
  const pollRef = useRef(null);

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));

  const load = () => api.get("/td/devices").then((r) => setDevices(r.data || [])).catch(() => {});
  const loadReadings = (id) => id && api.get(`/td/readings?device_id=${id}&limit=200`).then((r) => setReadings(r.data || [])).catch(() => {});

  useEffect(() => {
    load();
    api.get("/td/ingest-info").then((r) => setIngest(r.data)).catch(() => setIngest(null));
    pollRef.current = setInterval(() => { load(); if (chartId) loadReadings(chartId); }, 10000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (chartId) loadReadings(chartId); }, [chartId]);

  const resetForm = () => { setEditId(null); setForm(emptyD()); setShowForm(false); };
  const edit = (d) => { setEditId(d.id); setForm({ naam: d.naam || "", tag: d.tag || "", eenheid: d.eenheid || "", min: d.min ?? "", max: d.max ?? "" }); setShowForm(true); };

  const save = async () => {
    if (!form.naam.trim()) return toast.error("Vul een naam in (bijv. Stoomketel 1).");
    if (!form.tag.trim()) return toast.error("Vul een tag in (bijv. PIC.204).");
    setBusy(true);
    try {
      const body = { naam: form.naam.trim(), tag: form.tag.trim(), eenheid: form.eenheid.trim(), min: num(form.min), max: num(form.max) };
      if (editId) await api.put(`/td/devices/${editId}`, body); else await api.post("/td/devices", body);
      toast.success(editId ? "Installatie opgeslagen" : "Installatie toegevoegd");
      resetForm(); load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const del = async (d) => { if (!window.confirm(`Installatie "${d.naam}" verwijderen? Alle meethistorie gaat ook weg.`)) return; try { await api.delete(`/td/devices/${d.id}`); load(); if (chartId === d.id) setChartId(null); } catch (e) { toast.error(formatApiError(e)); } };
  const simulate = async (d) => { try { await api.post(`/td/devices/${d.id}/simulate`); load(); if (chartId === d.id) loadReadings(d.id); toast.success("Testmeting toegevoegd"); } catch (e) { toast.error(formatApiError(e)); } };

  const copy = (txt) => { try { navigator.clipboard.writeText(txt); toast.success("Gekopieerd"); } catch { /* noop */ } };

  const chartDev = devices.find((d) => d.id === chartId);
  const chartData = readings.map((r) => ({ t: new Date(r.ts).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }), waarde: r.value, setpoint: r.setpoint }));

  const statusOf = (d) => {
    if (d.laatste_waarde == null) return { dot: "bg-slate-300", ring: "border-slate-200", label: "Geen data" };
    if (d.laatste_alarm) return { dot: "bg-rose-500 animate-pulse", ring: "border-rose-300", label: "Buiten grens" };
    return { dot: "bg-emerald-500", ring: "border-emerald-200", label: "In orde" };
  };

  const curlVoorbeeld = ingest ? `curl -X POST "${backend}/api/td/ingest" \\
  -H "Content-Type: application/json" \\
  -H "${ingest.header}: ${ingest.key}" \\
  -d '{"tag": "PIC.204", "value": 3.9, "setpoint": 6.0}'` : "";

  return (
    <div className="space-y-3" data-testid="td-live">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-bold text-slate-800 inline-flex items-center gap-2"><Activity className="h-5 w-5 text-blue-600" /> Live installatiedata <span className="text-slate-400 font-normal">({devices.length})</span></h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" data-testid="td-koppel-toggle" onClick={() => setShowKoppel((v) => !v)}><Cable className="h-4 w-4 mr-1" /> Koppelen{showKoppel ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}</Button>
          <Button size="sm" data-testid="td-add-toggle" className="bg-[#1C64B0] hover:bg-[#154E8A]" onClick={() => { setShowForm((v) => !v); setEditId(null); setForm(emptyD()); }}><Plus className="h-4 w-4 mr-1" /> Installatie</Button>
        </div>
      </div>

      {/* Tegels */}
      {devices.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500" data-testid="td-empty">
          Nog geen installaties gekoppeld. Voeg bijv. je stoomketel toe (naam + tag <b>PIC.204</b>) en test met "Simuleer meting".
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {devices.map((d) => {
            const st = statusOf(d);
            return (
              <div key={d.id} data-testid="td-tile" className={`rounded-xl border-2 ${st.ring} bg-white p-4`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-slate-800 truncate inline-flex items-center gap-2"><Gauge className="h-4 w-4 text-slate-400" />{d.naam}</div>
                    <div className="text-xs font-mono text-slate-400">{d.tag}</div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500"><span className={`h-2.5 w-2.5 rounded-full ${st.dot}`} /> {st.label}</span>
                </div>
                <div className="mt-3 flex items-end gap-2">
                  <span data-testid="td-tile-value" className={`text-4xl font-extrabold tabular-nums ${d.laatste_alarm ? "text-rose-600" : "text-slate-900"}`}>{d.laatste_waarde != null ? d.laatste_waarde : "—"}</span>
                  <span className="text-sm font-semibold text-slate-500 mb-1">{d.eenheid}</span>
                </div>
                <div className="mt-1 text-xs text-slate-500 flex flex-wrap gap-x-3">
                  {d.laatste_setpoint != null && <span>SP: <b className="text-slate-700">{d.laatste_setpoint} {d.eenheid}</b></span>}
                  {(d.min !== "" && d.min != null) || (d.max !== "" && d.max != null) ? <span>grens: {d.min ?? "–"}…{d.max ?? "–"}</span> : null}
                  <span>{geleden(d.laatste_ts)}</span>
                </div>
                <div className="mt-3 flex items-center gap-1">
                  <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="td-simulate" onClick={() => simulate(d)}><Zap className="h-3.5 w-3.5 mr-1" /> Simuleer meting</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="td-chart-btn" onClick={() => setChartId(chartId === d.id ? null : d.id)}><LineIcon className="h-3.5 w-3.5 mr-1" /> Grafiek</Button>
                  <span className="flex-1" />
                  <Button size="icon" variant="ghost" className="h-7 w-7" data-testid="td-edit" onClick={() => edit(d)}><Pencil className="h-3.5 w-3.5 text-slate-500" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" data-testid="td-delete" onClick={() => del(d)}><Trash2 className="h-3.5 w-3.5 text-slate-400" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Grafiek */}
      {chartDev && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="td-chart">
          <div className="text-sm font-bold text-slate-700 mb-2">{chartDev.naam} — verloop ({readings.length} metingen)</div>
          {readings.length === 0 ? <p className="text-sm text-slate-400">Nog geen metingen. Klik op "Simuleer meting" of koppel de gateway.</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chartData} margin={{ top: 5, right: 12, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="t" tick={{ fontSize: 11 }} minTickGap={24} />
                <YAxis tick={{ fontSize: 11 }} domain={["auto", "auto"]} />
                <Tooltip />
                {chartDev.min != null && chartDev.min !== "" && <ReferenceLine y={Number(chartDev.min)} stroke="#f43f5e" strokeDasharray="4 4" />}
                {chartDev.max != null && chartDev.max !== "" && <ReferenceLine y={Number(chartDev.max)} stroke="#f43f5e" strokeDasharray="4 4" />}
                <Line type="monotone" dataKey="setpoint" stroke="#94a3b8" strokeWidth={1.5} dot={false} name="Setpoint" />
                <Line type="monotone" dataKey="waarde" stroke="#1C64B0" strokeWidth={2} dot={false} name="Waarde" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {/* Installatie toevoegen/bewerken */}
      {showForm && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="td-form">
          <div className="text-sm font-bold text-slate-700">{editId ? "Installatie bewerken" : "Nieuwe installatie"}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="lg:col-span-2"><label className="block text-xs font-semibold text-slate-600 mb-1">Naam</label><Input data-testid="td-f-naam" value={form.naam} onChange={(e) => setF({ naam: e.target.value })} placeholder="Stoomketel 1" /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Tag</label><Input data-testid="td-f-tag" value={form.tag} onChange={(e) => setF({ tag: e.target.value })} placeholder="PIC.204" /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Eenheid</label><Input data-testid="td-f-eenheid" value={form.eenheid} onChange={(e) => setF({ eenheid: e.target.value })} placeholder="bar" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Min</label><Input data-testid="td-f-min" type="number" value={form.min} onChange={(e) => setF({ min: e.target.value })} placeholder="5.5" /></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Max</label><Input data-testid="td-f-max" type="number" value={form.max} onChange={(e) => setF({ max: e.target.value })} placeholder="6.5" /></div>
            </div>
          </div>
          <p className="text-xs text-slate-400">De <b>tag</b> koppelt de meting aan deze installatie. Min/max bepalen wanneer een waarde als "buiten grens" (alarm) wordt getoond.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="td-cancel" onClick={resetForm}>Annuleren</Button>
            <Button disabled={busy} data-testid="td-save" onClick={save} className="bg-[#1C64B0] hover:bg-[#154E8A]">{busy ? "Opslaan…" : editId ? "Opslaan" : "Toevoegen"}</Button>
          </div>
        </div>
      )}

      {/* Koppel-instructies */}
      {showKoppel && (
        <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 space-y-2 text-sm" data-testid="td-koppel">
          <div className="font-bold text-slate-800 inline-flex items-center gap-2"><Cable className="h-4 w-4 text-blue-600" /> Echte ketel koppelen (live data)</div>
          <p className="text-slate-600">De JUMO dTRON 308 heeft een optionele <b>RS485 / Modbus RTU</b>-interface nodig. Een klein bruggetje (gateway/Raspberry Pi) leest die uit en stuurt elke paar seconden de waarde naar onderstaand adres. Er hoeft niets in je netwerk opengezet te worden — de gateway pusht naar buiten.</p>
          {ingest && ingest.configured ? (
            <>
              <div className="grid gap-2">
                <div><div className="text-xs font-semibold text-slate-500">Push-URL (POST)</div><div className="flex items-center gap-2"><code className="flex-1 rounded bg-white border border-slate-200 px-2 py-1 text-xs break-all">{backend}/api/td/ingest</code><Button size="icon" variant="ghost" className="h-7 w-7" data-testid="td-copy-url" onClick={() => copy(`${backend}/api/td/ingest`)}><Copy className="h-3.5 w-3.5" /></Button></div></div>
                <div><div className="text-xs font-semibold text-slate-500">Sleutel (header <code>{ingest.header}</code>)</div><div className="flex items-center gap-2"><code className="flex-1 rounded bg-white border border-slate-200 px-2 py-1 text-xs break-all" data-testid="td-key">{ingest.key}</code><Button size="icon" variant="ghost" className="h-7 w-7" data-testid="td-copy-key" onClick={() => copy(ingest.key)}><Copy className="h-3.5 w-3.5" /></Button></div></div>
                <div><div className="text-xs font-semibold text-slate-500">Voorbeeld</div><pre className="rounded bg-slate-900 text-slate-100 p-3 text-[11px] overflow-x-auto whitespace-pre-wrap">{curlVoorbeeld}</pre></div>
              </div>
              <p className="text-xs text-slate-500">Stuur <code>tag</code> gelijk aan de tag van de installatie hierboven (bijv. <b>PIC.204</b>), plus <code>value</code> (huidige waarde) en optioneel <code>setpoint</code>.</p>
            </>
          ) : (
            <p className="text-slate-500">Koppel-sleutel is (nog) niet beschikbaar voor jouw rol, of niet geconfigureerd. Vraag de beheerder.</p>
          )}
        </div>
      )}
    </div>
  );
}
